/**
 * The analytics-consent banner on the public pages, plus the "Cookie settings" link that reopens
 * it. Mounted once, in App's public-host branch, so every public page (event, landing, journey,
 * appointment booking, portal register) records its page load and shows the same banner.
 *
 * Data: unbound (cookies only, see public-touch.ts).
 * Copy keys (suggested; CC extracts and sends the catalog patch): public.consent.banner,
 * public.consent.accept, public.consent.decline, public.consent.settings.
 */
import { useEffect, useSyncExternalStore } from "react";
import { Btn } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import { consentBannerOpen, recordPublicPageLoad, reopenConsentBanner, setAnalyticsConsent, subscribeConsent } from "./public-touch";

export function PublicConsentHost({ route }: { route: string }) {
  const t = useT();
  const href = typeof window === "undefined" ? "" : window.location.href;
  useEffect(() => { recordPublicPageLoad(); }, [route, href]);
  const open = useSyncExternalStore(subscribeConsent, consentBannerOpen, () => false);
  return (
    <>
      <div data-testid="public-consent-settings" style={{ display: "flex", justifyContent: "center", padding: "12px 16px 20px" }}>
        <button
          type="button"
          onClick={reopenConsentBanner}
          data-testid="public-consent-settings-link"
          style={{ background: "none", border: 0, padding: 4, cursor: "pointer", font: "inherit", fontSize: 13, color: "var(--jrni-color-neutral-2)", textDecoration: "underline" }}
        >
          {t("public.consent.settings", "Cookie settings")}
        </button>
      </div>
      {open ? (
        <div
          role="region"
          aria-label={t("public.consent.settings", "Cookie settings")}
          data-testid="public-consent-banner"
          style={{ position: "fixed", left: 16, right: 16, bottom: 16, zIndex: 900, display: "flex", justifyContent: "center", pointerEvents: "none" }}
        >
          <div style={{ pointerEvents: "auto", width: "100%", maxWidth: 640, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--jrni-color-neutral-white, #fff)", border: "1px solid var(--jrni-color-neutral-4, #E2E4E9)", borderRadius: 12, boxShadow: "0 8px 24px rgba(17,24,39,.12)", fontFamily: "var(--jrni-font-family-sans)" }}>
            <p style={{ margin: 0, flex: "1 1 260px", minWidth: 0, fontSize: 14, lineHeight: 1.45, color: "var(--jrni-color-neutral-1)" }}>
              {t("public.consent.banner", "We use an analytics cookie to record which link brought you here.")}
            </p>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <Btn variant="secondary" size="md" onClick={() => setAnalyticsConsent(false)} data-testid="public-consent-decline">{t("public.consent.decline", "Decline")}</Btn>
              <Btn variant="primary" size="md" onClick={() => setAnalyticsConsent(true)} data-testid="public-consent-accept">{t("public.consent.accept", "Accept")}</Btn>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
